import { useState, useEffect, useMemo } from 'react';
import {
  getStates,
  getSubjectsFromClasses,
  getAllSubjectsFromAPI,
  clearClassesResponseCache,
  clearSubjectsResponseCache,
  clearAllSubjectsResponseCache,
  isLoggedIn,
} from '../service';
import { getLocalStorage, getUserStates } from '../utils/function';
import { regionDefaultState } from '../utils/lexicon';
import { filterSubjectsByStates, normalizeStateCodes } from '../utils/subjects';
import {
  getTaskTypesForSubjects,
  getTaskTypesForSubjectsWithYears,
  getResponseTypesForSubjects,
  getResponseTypesForSubjectsWithYears,
  getAllTaskTypeLabels,
  getAllResponseTypeLabels,
} from '../utils/typeMappings';

const cache = {
  subjects: {},
  states: null,
  availableYears: null,
  apiSubjects: null,
  allApiSubjects: null,
  classSubjects: null,
};

const normalizeYearNum = (y) => {
  if (y == null) return null;
  if (typeof y === 'number' && !Number.isNaN(y)) return y;
  const yearStr = String(y).replace(/^Year-?\s*/i, '').trim();
  const n = parseInt(yearStr, 10);
  return Number.isNaN(n) ? null : n;
};

const yearMatches = (yearsArr, yearNum) => {
  if (yearNum == null || !Array.isArray(yearsArr) || !yearsArr.length) return true;
  return yearsArr.some((y) => Number(y) === yearNum || y === yearNum);
};

const formatApiSubject = (s) => ({
  title: s.subjectName || s.name || s.title,
  code: s.subjectCode || s.code,
  taskTypes: s.taskTypes || [],
  responseTypes: [...new Set((s.taskTypes || []).flatMap((t) => t.responseTypes || []))],
});

export const useApiSubjects = () => {
  const isAuthenticated = isLoggedIn();
  const [apiSubjects, setApiSubjects] = useState(cache.apiSubjects || []);
  const [loading, setLoading] = useState(isAuthenticated && !cache.apiSubjects);

  useEffect(() => {
    // Public feedback views also mount subject pickers. Do not fetch or cache
    // an anonymous result; a later sign-in must still load the real catalog.
    if (!isAuthenticated) {
      setApiSubjects([]);
      setLoading(false);
      return;
    }
    if (cache.apiSubjects) {
      setApiSubjects(cache.apiSubjects);
      setLoading(false);
      return;
    }
    setLoading(true);
    getSubjectsFromClasses()
      .then((list) => {
        cache.apiSubjects = list;
        setApiSubjects(list);
      })
      .catch(() => setApiSubjects([]))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  return { apiSubjects, loading };
};

const resolveStates = (stateOrStates) => {
  if (Array.isArray(stateOrStates) && stateOrStates.length > 0) return stateOrStates;
  if (stateOrStates) return [stateOrStates];
  const stored = getUserStates();
  return stored.length > 0 ? stored : [regionDefaultState(getLocalStorage('country'))];
};

export const useSubjects = (state, year) => {
  const { apiSubjects, loading } = useApiSubjects();
  const userStates = resolveStates(state);
  const userYear = year || getLocalStorage('year');
  const yearNum = normalizeYearNum(userYear);
  const stateNorms = userStates.map((s) => s?.toUpperCase?.()?.trim()).filter(Boolean);

  const formattedSubjects = useMemo(() => {
    const filtered = (apiSubjects || []).filter((s) => {
      if (stateNorms.length && (!s.states?.length || !s.states.some((st) => stateNorms.includes(String(st).toUpperCase())))) return false;
      if (yearNum != null && (!s.years?.length || !yearMatches(s.years, yearNum))) return false;
      return true;
    });
    return filtered.map(formatApiSubject);
  }, [apiSubjects, stateNorms, yearNum]);

  return {
    subjects: formattedSubjects,
    rawSubjects: apiSubjects,
    loading,
    error: null,
  };
};

// `states` is an explicit, OPTIONAL scope: pass the individual teacher's
// state(s) (via useMarkingToolStates) to restrict the picker to their state;
// omit it (or pass []) and the result is the full year-filtered catalog exactly
// as before. Cache is keyed by state too, so a state change never returns
// another state's cached list.
const cacheSubjectsByYearFromClasses = {};
export const useSubjectsForYearFromClasses = (year, states) => {
  const { apiSubjects } = useApiSubjects();
  const yearNum = normalizeYearNum(year);
  const stateKey = normalizeStateCodes(states).join(',');

  const subjects = useMemo(() => {
    if (!year || !apiSubjects?.length) return [];
    const cacheKey = `${year}|${stateKey}`;
    if (cacheSubjectsByYearFromClasses[cacheKey]) return cacheSubjectsByYearFromClasses[cacheKey];
    const stateNorms = stateKey ? stateKey.split(',') : [];
    const filtered = filterSubjectsByStates(apiSubjects, stateNorms).filter(
      (s) => !s.years?.length || yearNum == null || yearMatches(s.years, yearNum)
    );
    const result = filtered.map(formatApiSubject).sort((a, b) => a.title.localeCompare(b.title));
    cacheSubjectsByYearFromClasses[cacheKey] = result;
    return result;
  }, [year, apiSubjects, yearNum, stateKey]);

  return { subjects };
};

export const useSubjectsForYearsFromClasses = (years, states) => {
  const { apiSubjects } = useApiSubjects();
  const yearNums = useMemo(() => {
    const arr = Array.isArray(years) ? years : (years != null ? [years] : []);
    return arr.map(normalizeYearNum).filter((n) => n != null);
  }, [years]);
  const stateKey = normalizeStateCodes(states).join(',');

  const subjects = useMemo(() => {
    if (!apiSubjects?.length || !yearNums.length) return [];
    const stateNorms = stateKey ? stateKey.split(',') : [];
    const filtered = filterSubjectsByStates(apiSubjects, stateNorms).filter((s) =>
      !s.years?.length || yearNums.some((yearNum) => yearMatches(s.years, yearNum))
    );
    const byCode = new Map();
    filtered.map(formatApiSubject).forEach((s) => {
      const code = s.code || s.subjectCode;
      if (code && !byCode.has(code)) byCode.set(code, s);
    });
    return Array.from(byCode.values()).sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [apiSubjects, yearNums, stateKey]);

  return { subjects };
};

// All catalog subjects (no year filter) scoped to the given state(s). Used as
// the create-flow fallback when the year-filtered list is empty: passing the
// individual teacher's scope keeps the fallback state-scoped (never reopening
// the whole-country catalog); passing [] returns every subject, matching the
// previous useClassSubjects() fallback for school/stateless users.
export const useUserStateSubjects = (states) => {
  const { apiSubjects } = useApiSubjects();
  const stateKey = normalizeStateCodes(states).join(',');

  const subjects = useMemo(() => {
    const stateNorms = stateKey ? stateKey.split(',') : [];
    return filterSubjectsByStates(apiSubjects || [], stateNorms)
      .map(formatApiSubject)
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [apiSubjects, stateKey]);

  return { subjects };
};

const toSubjectNames = (subjects) => {
  const arr = Array.isArray(subjects) ? subjects : (subjects ? [subjects] : []);
  return arr.map((s) => (typeof s === 'object' ? (s.subjectName ?? s.name ?? s.title ?? s.subjectCode ?? s.code) : s)).filter(Boolean);
};

export const useTaskTypes = (subjects, state, yearOrYears) => {
  const { apiSubjects, loading } = useApiSubjects();
  const userState = state || getLocalStorage('state') || regionDefaultState(getLocalStorage('country'));
  const yearsArr = Array.isArray(yearOrYears) ? yearOrYears : (yearOrYears != null ? [yearOrYears] : []);
  const singleYear = yearsArr.length === 1 ? yearsArr[0] : yearsArr.length ? undefined : (yearOrYears || getLocalStorage('year'));

  const formattedTaskTypes = useMemo(() => {
    const names = toSubjectNames(subjects);
    const filtered = yearsArr.length > 1
      ? getTaskTypesForSubjectsWithYears(apiSubjects || [], names, userState, yearsArr)
      : getTaskTypesForSubjects(apiSubjects || [], names, userState, singleYear || getLocalStorage('year'));
    return filtered.map((t) => ({ title: t.name || t.code, value: t.code }));
  }, [apiSubjects, subjects, userState, singleYear, yearsArr]);

  return { taskTypes: formattedTaskTypes, rawTaskTypes: formattedTaskTypes, loading, error: null };
};

export const useResponseTypes = (subjects, taskType, state, yearOrYears) => {
  const { apiSubjects, loading } = useApiSubjects();
  const userState = state || getLocalStorage('state') || regionDefaultState(getLocalStorage('country'));
  const yearsArr = Array.isArray(yearOrYears) ? yearOrYears : (yearOrYears != null ? [yearOrYears] : []);
  const singleYear = yearsArr.length === 1 ? yearsArr[0] : yearsArr.length ? undefined : (yearOrYears || getLocalStorage('year'));

  const formattedResponseTypes = useMemo(() => {
    const names = toSubjectNames(subjects);
    const taskTypes = Array.isArray(taskType) ? taskType : (taskType ? [taskType] : []);
    const filtered = yearsArr.length > 1
      ? getResponseTypesForSubjectsWithYears(apiSubjects || [], names, taskTypes, userState, yearsArr)
      : getResponseTypesForSubjects(apiSubjects || [], names, taskTypes, userState, singleYear || getLocalStorage('year'));
    return filtered.map((t) => ({
      value: t.code,
      label: t.name || t.code,
      tooltip: t.tooltip || '',
    }));
  }, [apiSubjects, subjects, taskType, userState, singleYear, yearsArr]);

  return {
    responseTypes: formattedResponseTypes,
    rawResponseTypes: formattedResponseTypes,
    loading,
    error: null,
  };
};


export const useAvailableStates = () => {
  // Seed from the cache and report loading from the very first render — the old
  // initial `loading: false` opened a one-commit window where consumers (e.g. the
  // draft-settings popup init) saw "not loading" with an empty states list and
  // resolved state from an incomplete fallback.
  const [states, setStates] = useState(cache.states || []);
  const [loading, setLoading] = useState(!cache.states);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStates = async () => {
      if (cache.states) {
        setStates(cache.states);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await getStates();
        const statesList = Array.isArray(data) ? data : data?.states || [];
        statesList.sort((a, b) => {
          const ai = AU_STATE_ORDER.indexOf(a);
          const bi = AU_STATE_ORDER.indexOf(b);
          return (ai === -1 ? AU_STATE_ORDER.length : ai) - (bi === -1 ? AU_STATE_ORDER.length : bi);
        });
        cache.states = statesList;
        setStates(statesList);
      } catch (err) {
        setError(err);
        setStates([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStates();
  }, []);

  return { states, loading, error };
};

export const useAvailableYears = () => {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchYears = async () => {
      if (cache.availableYears) {
        setYears(cache.availableYears);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const subjects = await getSubjectsFromClasses();
        const allYears = new Set();
        (Array.isArray(subjects) ? subjects : []).forEach((s) => {
          (s.years || []).forEach((y) => allYears.add(y));
        });
        const yearsArray = Array.from(allYears).sort((a, b) => (Number(a) || 0) - (Number(b) || 0));
        cache.availableYears = yearsArray;
        setYears(yearsArray);
      } catch (err) {
        setError(err);
        setYears([]);
      } finally {
        setLoading(false);
      }
    };
    fetchYears();
  }, []);

  return { years, loading, error };
};

const AU_STATE_ORDER = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'NT', 'TAS', 'ACT'];

const FALLBACK_COUNTRIES = {
  Australia: AU_STATE_ORDER,
};

// Unfiltered subjects (all countries/states) — used by the onboarding location picker so a
// brand-new user can pick a country before their (AU-defaulted) profile filters the catalog.
export const useAllApiSubjects = () => {
  const [allApiSubjects, setAllApiSubjects] = useState(cache.allApiSubjects || []);
  const [loading, setLoading] = useState(!cache.allApiSubjects);

  useEffect(() => {
    if (cache.allApiSubjects) {
      setAllApiSubjects(cache.allApiSubjects);
      setLoading(false);
      return;
    }
    getAllSubjectsFromAPI()
      .then((list) => {
        // Don't cache an empty/failed result — keep retrying on later mounts so a transient
        // error never locks a US user into the AU-only fallback for the whole session.
        if (list?.length) cache.allApiSubjects = list;
        setAllApiSubjects(list || []);
      })
      .catch(() => setAllApiSubjects([]))
      .finally(() => setLoading(false));
  }, []);

  return { allApiSubjects, loading };
};

export const useAvailableCountries = () => {
  const { allApiSubjects, loading } = useAllApiSubjects();

  const countriesAndStates = useMemo(() => {
    if (!allApiSubjects?.length) return loading ? {} : FALLBACK_COUNTRIES;
    const result = {};
    allApiSubjects.forEach((s) => {
      const countries = s.countries?.length ? s.countries : ['Australia'];
      const states = s.states || [];
      countries.forEach((country) => {
        if (!result[country]) result[country] = new Set();
        states.forEach((st) => result[country].add(st));
      });
    });
    const sorted = {};
    Object.keys(result).sort().forEach((country) => {
      const states = Array.from(result[country]);
      if (country === 'Australia') {
        states.sort((a, b) => {
          const ai = AU_STATE_ORDER.indexOf(a);
          const bi = AU_STATE_ORDER.indexOf(b);
          return (ai === -1 ? AU_STATE_ORDER.length : ai) - (bi === -1 ? AU_STATE_ORDER.length : bi);
        });
      } else {
        states.sort();
      }
      sorted[country] = states;
    });
    return Object.keys(sorted).length > 0 ? sorted : FALLBACK_COUNTRIES;
  }, [allApiSubjects, loading]);

  return { countriesAndStates, loading };
};

export const useYearsByState = (state) => {
  const { apiSubjects, loading } = useApiSubjects();
  const stateNorm = state?.toUpperCase?.()?.trim();

  const years = useMemo(() => {
    if (!apiSubjects?.length) return [];
    const filtered = stateNorm
      ? apiSubjects.filter((s) => !s.states?.length || s.states.some((st) => String(st).toUpperCase() === stateNorm))
      : apiSubjects;
    const allYears = new Set();
    filtered.forEach((s) => {
      (s.years || []).forEach((y) => allYears.add(y));
    });
    return Array.from(allYears).sort((a, b) => (Number(a) || 0) - (Number(b) || 0));
  }, [apiSubjects, stateNorm]);

  return { years, loading };
};

// Labels are now derived dynamically from API subjects using getAllTaskTypeLabels/getAllResponseTypeLabels
// Use useClassTaskTypes() and useClassResponseTypes() hooks to get labels with their associated values

export const useClassTaskTypes = () => {
  const { apiSubjects, loading } = useApiSubjects();
  const taskTypes = useMemo(() => {
    const labels = getAllTaskTypeLabels(apiSubjects || []);
    return Object.entries(labels).map(([value, label]) => ({ value, label }));
  }, [apiSubjects]);

  return { taskTypes, loading };
};

export const useClassSubjects = () => {
  const { apiSubjects, loading } = useApiSubjects();

  const subjects = useMemo(() => {
    if (cache.classSubjects && cache.classSubjects.length === (apiSubjects || []).length) return cache.classSubjects;
    const list = (apiSubjects || []).map(formatApiSubject).sort((a, b) => a.title.localeCompare(b.title));
    cache.classSubjects = list;
    return list;
  }, [apiSubjects]);

  return { subjects, loading };
};

export const useClassResponseTypes = () => {
  const { apiSubjects, loading } = useApiSubjects();
  const responseTypes = useMemo(() => {
    const labels = getAllResponseTypeLabels(apiSubjects || []);
    return Object.entries(labels).map(([value, label]) => ({ value, label }));
  }, [apiSubjects]);

  return { responseTypes, loading };
};

export const clearSubjectsCache = () => {
  cache.subjects = {};
  cache.states = null;
  cache.availableYears = null;
  cache.apiSubjects = null;
  cache.allApiSubjects = null;
  cache.classSubjects = null;
  clearClassesResponseCache();
  clearSubjectsResponseCache();
  clearAllSubjectsResponseCache();
  Object.keys(cacheSubjectsByYearFromClasses).forEach((k) => delete cacheSubjectsByYearFromClasses[k]);
};

export const getSubjectCode = (subjects, subjectTitle) => {
  if (!subjects || !Array.isArray(subjects)) return '';
  const subject = subjects.find(
    (s) => (s.subjectName || s.name || s.title) === subjectTitle
  );
  return subject?.subjectCode || subject?.code || '';
};
